<template>
  <main class="care-progress-page">
    <header class="page-header">
      <button class="header-icon" type="button" aria-label="返回" @click="goBack">
        <van-icon name="arrow-left" />
      </button>
      <div class="header-title">我的照护</div>
      <button class="header-icon" type="button" aria-label="更多服务说明" @click="showServiceTip">
        <van-icon name="question-o" />
      </button>
    </header>

    <section class="baby-switcher" aria-label="选择宝宝">
      <button
        v-for="baby in babies"
        :key="baby.id"
        type="button"
        class="baby-option"
        :class="{ active: selectedBaby === baby.id }"
        @click="selectedBaby = baby.id"
      >
        <span class="baby-avatar" :class="baby.tone">{{ baby.initial }}</span>
        <span>{{ baby.name }}</span>
      </button>
      <span class="service-window">180 天照护 · 第 2 周</span>
    </section>

    <section class="welcome-block">
      <p class="eyebrow">{{ currentBaby.name }}的照护旅程</p>
      <h1>先由专业的人看见，<br>再给宝宝合适的指导。</h1>
      <p class="welcome-copy">这一周先完成首次专业评估。体格、喂养与神经发育需要一起判断。</p>
    </section>

    <section class="physical-foundation">
      <div class="foundation-head">
        <div>
          <p class="section-kicker">体格基础</p>
          <h2>已建立，继续观察</h2>
        </div>
        <span class="foundation-state"><van-icon name="passed" /> 状态平稳</span>
      </div>
      <div class="foundation-metrics">
        <span><b>3.62</b> kg<small>最近体重</small></span>
        <span><b>7</b> 天<small>本周已记录</small></span>
        <span><b>良好</b><small>吃奶与休息</small></span>
      </div>
    </section>

    <section class="growth-section current-status-section">
      <div class="section-heading">
        <div>
          <p class="section-kicker">01 · 宝宝现状</p>
          <h2>今天适合完成首次专业评估</h2>
        </div>
        <span class="data-state">本周状态平稳</span>
      </div>
      <div class="growth-panel">
        <div class="growth-chart" aria-label="近期体重趋势">
          <span class="chart-label label-top">3.8 kg</span>
          <span class="chart-label label-bottom">3.0 kg</span>
          <div class="chart-grid"></div>
          <div class="chart-point"><span></span><b>今日</b></div>
        </div>
        <div class="growth-note">
          <span class="note-mark">01</span>
          <p>体重、吃奶和休息状态平稳，适合照护师进行首次评估。神经发育任务会在评估和示范后，按宝宝状态发布。</p>
        </div>
      </div>
    </section>

    <section class="professional-loop-section">
      <div class="section-heading">
        <div>
          <p class="section-kicker">180 天专业照护</p>
          <h2>先评估，再制定宝宝的照护路径</h2>
        </div>
      </div>
      <div class="professional-loop">
        <div class="professional-head"><span class="caregiver-avatar small">李</span><div><b>李欣然 · 新生儿照护师</b><p>本周将先完成首次上门专业评估与示范。</p></div><span class="review-state">待预约</span></div>
        <div class="care-loop-steps" aria-label="照护服务闭环">
          <span class="active">首次评估</span><i></i><span>计划指导</span><i></i><span>家庭反馈</span><i></i><span>持续复核</span>
        </div>
        <p class="professional-note">神经发育任务、视频反馈和后续上门都以本次评估为起点；照护师会综合体格、喂养、姿势和行为反应后安排。</p>
      </div>
      <div class="package-assurance" aria-label="半年服务包保障">
        <span><b>首访 + 5</b><small>专业上门指导</small></span><span><b>每周</b><small>主动电话回访</small></span><span><b>7×24</b><small>图文支持</small></span>
      </div>
    </section>

    <section class="care-now-section">
      <div class="section-heading">
        <div>
          <p class="section-kicker">02 · 本周专业服务</p>
          <h2>照护师会这样推进宝宝的照护</h2>
        </div>
      </div>
      <article class="current-service-card assessment-service">
        <div class="service-card-head">
          <span class="service-index">当前最重要</span>
          <span class="appointment-label">{{ visitRequested ? '等待确认' : '需要你预约' }}</span>
        </div>
        <div class="task-main">
          <div class="assessment-icon"><van-icon name="notes-o" /></div>
          <div class="task-copy">
            <h2>首次上门专业评估与指导</h2>
            <p>照护师到家里看真实照护过程，判断现在该先解决什么、怎么做才适合宝宝。</p>
          </div>
        </div>
        <div class="assessment-items"><span>体格与追赶节奏</span><span>吞咽与喂养姿势</span><span>张力、姿势与动作</span><span>睡眠环境与照护方式</span></div>
        <div class="visit-window"><van-icon name="clock-o" /> {{ visitRequested ? '预约已提交，照护师会和你确认上门时间' : '可预约窗口：7 月 22 日 - 7 月 28 日' }}</div>
        <button v-if="!visitRequested" class="secondary-action full-action" type="button" @click="showVisitSheet = true">选择上门时间 <van-icon name="arrow" /></button>
      </article>

      <article class="current-service-card video-service">
        <div class="service-card-head"><span class="service-index">持续服务</span><span class="video-label">本周自动安排</span></div>
        <div class="visit-service-body">
          <div class="video-icon"><van-icon name="phone-o" /></div>
          <div><h2>每周主动电话回访</h2><p>不是固定打卡电话。照护师会主动确认吃奶、睡眠、排便、反流和家庭照护中的变化。</p></div>
        </div>
        <div class="visit-window"><van-icon name="clock-o" /> {{ callbackRequested ? '已申请提前回访，照护师会优先确认联系时间' : '本周内由照护师主动联系，也可申请提前回访' }}</div>
        <button v-if="!callbackRequested" class="secondary-action full-action" type="button" @click="showCallbackSheet = true">申请提前回访 <van-icon name="arrow" /></button>
      </article>

      <article class="current-service-card visit-service">
        <div class="service-card-head">
          <span class="service-index">持续支持</span>
          <span class="active-label">随时可用</span>
        </div>
        <div class="visit-service-body">
          <div class="visit-icon"><van-icon name="chat-o" /></div>
          <div><h2>7×24 图文咨询支持</h2><p>吃奶突降、反流、异常哭闹、训练不确定等时间敏感问题，可以先联系照护师，不必等到下次上门或回访。</p></div>
        </div>
        <button class="secondary-action full-action" type="button" @click="contactCaregiver">联系照护师 <van-icon name="arrow" /></button>
      </article>
    </section>

    <section class="upcoming-section">
      <div class="section-heading">
        <div>
          <p class="section-kicker">03 · 评估后会接续</p>
          <h2>照护师会把评估变成具体指导</h2>
        </div>
      </div>
      <div class="upcoming-list">
        <article class="upcoming-item callback-item">
          <span class="upcoming-date">评估后 24 小时内</span>
          <div class="upcoming-copy"><span class="service-state">照护师发布</span><h3>个性化照护计划</h3><p>把体格、喂养、睡眠和神经发育重点整理成家庭可以执行的计划与观察点。</p></div>
        </article>
        <article class="upcoming-item">
          <span class="upcoming-date">计划发布后</span>
          <div class="upcoming-copy"><span class="service-state">按指导配合</span><h3>视频反馈与专业复核</h3><p>照护师会明确拍什么、什么时候拍；视频只作为复核输入，不替代上门评估。</p></div>
          <button v-if="!videoSubmitted" class="link-action" type="button" @click="showVideoSheet = true">查看视频要求</button>
        </article>
        <article class="upcoming-item">
          <span class="upcoming-date">关键窗口或需要时</span>
          <div class="upcoming-copy"><span class="service-state">专业复访</span><h3>后续上门指导</h3><p>结合矫正月龄、家庭执行、体格变化与风险情况安排，不机械平均分配次数。</p></div>
        </article>
      </div>
    </section>

    <section class="timeline-section completed-section">
      <div class="section-heading timeline-heading">
        <div>
          <p class="section-kicker">已获得</p>
          <h2>照护师已交付给你的内容</h2>
        </div>
      </div>

      <div class="timeline-list completed-list">
        <article
          v-for="(service, index) in visibleServices"
          :key="service.id"
          class="service-item"
          :class="[service.status, { open: openServiceId === service.id }]"
        >
          <div class="timeline-rail" aria-hidden="true">
            <span class="timeline-node"><van-icon :name="service.icon" /></span>
            <span v-if="index !== visibleServices.length - 1" class="timeline-line"></span>
          </div>
          <div class="service-content">
            <button class="service-summary" type="button" @click="toggleService(service.id)">
              <div class="service-summary-copy">
                <span class="service-state">{{ service.state }}</span>
                <h3>{{ service.title }}</h3>
                <p>{{ service.summary }}</p>
              </div>
              <van-icon class="service-arrow" :name="openServiceId === service.id ? 'arrow-up' : 'arrow-down'" />
            </button>

            <div v-if="openServiceId === service.id" class="service-detail">
              <div class="detail-rule"></div>
              <div class="detail-row">
                <span>完成后你会得到</span>
                <strong>{{ service.result }}</strong>
              </div>
              <div class="detail-row">
                <span>由谁推进</span>
                <strong>{{ service.owner }}</strong>
              </div>
              <button v-if="service.action" class="secondary-action" type="button" @click="handleServiceAction(service)">
                {{ service.action }}
                <van-icon name="arrow" />
              </button>
              <button v-else-if="service.resultAvailable" class="result-link" type="button" @click="showResult(service)">
                查看服务成果
                <van-icon name="arrow" />
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="caregiver-note">
      <div class="caregiver-avatar">李</div>
      <div>
        <p class="section-kicker">你的照护师</p>
        <h2>李欣然 · 新生儿照护师</h2>
        <p>首次评估前、日常照护中有不确定，都可以随时联系我。</p>
      </div>
      <button type="button" class="phone-button" aria-label="联系照护师" @click="contactCaregiver">
        <van-icon name="phone-o" />
      </button>
    </section>

    <van-popup v-model="showTaskSheet" position="bottom" round class="weight-popup" :close-on-click-overlay="false">
      <form class="weight-form" @submit.prevent="submitTaskFeedback">
        <div class="popup-handle"></div>
        <div class="popup-heading">
          <div>
            <p class="section-kicker">本周核心任务</p>
            <h2>俯卧抬头观察</h2>
          </div>
          <button type="button" class="close-popup" aria-label="关闭" @click="showTaskSheet = false"><van-icon name="cross" /></button>
        </div>
        <p class="task-sheet-description">清醒、奶后 1 小时、状态平稳时，短时观察即可。头部短暂抬离床面就很好。</p>
        <label class="field-label">这一次宝宝的状态</label>
        <div class="feedback-options">
          <button v-for="option in feedbackOptions" :key="option.value" type="button" :class="{ selected: taskFeedback === option.value }" @click="taskFeedback = option.value">{{ option.label }}</button>
        </div>
        <div class="measurement-reminder">
          <van-icon name="info-o" />
          出现连续哭闹、头明显下垂或呼吸急促，请立即停止并选择“先暂停”。
        </div>
        <button class="submit-weight" type="submit">提交本次反馈</button>
      </form>
    </van-popup>

    <van-popup v-model="showVisitSheet" position="bottom" round class="action-popup">
      <div class="action-sheet">
        <div class="popup-handle"></div>
        <div class="popup-heading"><div><p class="section-kicker">首次上门访视</p><h2>选择方便的时间</h2></div><button type="button" class="close-popup" aria-label="关闭" @click="showVisitSheet = false"><van-icon name="cross" /></button></div>
        <p class="task-sheet-description">提交后，照护师会在上门前和你再次确认。若时间变化，可在服务中改期。</p>
        <div class="time-options"><button v-for="slot in visitSlots" :key="slot.value" type="button" :class="{ selected: selectedVisitSlot === slot.value }" @click="selectedVisitSlot = slot.value"><b>{{ slot.label }}</b><small>{{ slot.detail }}</small></button></div>
        <button class="submit-weight" type="button" @click="confirmVisit">提交预约</button>
      </div>
    </van-popup>

    <van-popup v-model="showCallbackSheet" position="bottom" round class="action-popup">
      <div class="action-sheet">
        <div class="popup-handle"></div>
        <div class="popup-heading"><div><p class="section-kicker">本周电话回访</p><h2>申请提前回访</h2></div><button type="button" class="close-popup" aria-label="关闭" @click="showCallbackSheet = false"><van-icon name="cross" /></button></div>
        <p class="task-sheet-description">回访不绑定固定时间。提交后，照护师会优先在可联系时间内回电。</p>
        <label class="field-label">方便接听的时间</label>
        <div class="feedback-options"><button v-for="option in callbackOptions" :key="option.value" type="button" :class="{ selected: selectedCallbackWindow === option.value }" @click="selectedCallbackWindow = option.value">{{ option.label }}</button></div>
        <button class="submit-weight" type="button" @click="requestCallback">提交申请</button>
      </div>
    </van-popup>

    <van-popup v-model="showVideoSheet" position="bottom" round class="action-popup" :close-on-click-overlay="false">
      <div class="action-sheet">
        <div class="popup-handle"></div>
        <div class="popup-heading"><div><p class="section-kicker">视频反馈与专业复核</p><h2>上传俯卧观察视频</h2></div><button type="button" class="close-popup" aria-label="关闭" @click="showVideoSheet = false"><van-icon name="cross" /></button></div>
        <p class="task-sheet-description">请在宝宝清醒、状态舒适时拍摄 20 - 60 秒。照护师会结合体格数据、家庭观察和后续上门/回访进行判断，不依据单段视频下结论。</p>
        <div class="video-upload-placeholder"><van-icon name="video-o" /><b>俯卧观察 · 待上传</b><small>示例原型不读取真实视频</small></div>
        <div class="measurement-reminder"><van-icon name="info-o" /> 连续哭闹、头明显下垂或呼吸急促时，请先停止拍摄并联系照护师。</div>
        <button class="submit-weight" type="button" @click="submitVideo">提交给照护师复核</button>
      </div>
    </van-popup>

    <van-popup v-model="showResultPopup" position="bottom" round class="result-popup">
      <div class="result-sheet">
        <div class="popup-handle"></div>
        <span class="result-badge">服务成果</span>
        <h2>{{ activeResult.title }}</h2>
        <p class="result-meta">服务时间 {{ activeResult.date }} · 适用于{{ currentBaby.name }}</p>
        <div class="result-content">
          <p class="result-label">本次完成</p>
          <p>{{ activeResult.completed }}</p>
          <p class="result-label">接下来</p>
          <p>{{ activeResult.next }}</p>
        </div>
        <button type="button" class="sheet-close" @click="showResultPopup = false">我知道了</button>
      </div>
    </van-popup>
  </main>
</template>

<script>
export default {
    name: 'ServiceProgress',
    data () {
        return {
            selectedBaby: 'baby-1',
            openServiceId: '',
            showTaskSheet: false,
            showVisitSheet: false,
            showCallbackSheet: false,
            showVideoSheet: false,
            showResultPopup: false,
            taskDone: false,
            taskFeedback: 'completed',
            visitRequested: false,
            callbackRequested: false,
            videoSubmitted: false,
            selectedVisitSlot: 'wed-am',
            selectedCallbackWindow: 'today-pm',
            activeResult: {},
            babies: [
                { id: 'baby-1', name: '小米粒', initial: '米', tone: 'mint' },
                { id: 'baby-2', name: '小团子', initial: '团', tone: 'peach' }
            ],
            services: [
                {
                    id: 'onboarding',
                    icon: 'passed',
                    state: '已完成',
                    status: 'done',
                    title: '照护服务已为宝宝开启',
                    summary: '李欣然照护师会陪伴宝宝度过关键的 180 天。',
                    result: '一位专属照护师和完整照护安排',
                    owner: '照护团队',
                    resultAvailable: true,
                    date: '2026.07.18',
                    completed: '已完成服务开通和照护师匹配。',
                    next: '体格基础稳定后，进入本月的家庭照护任务。'
                },
                {
                    id: 'weight',
                    icon: 'balance-o',
                    state: '已完成',
                    status: 'done',
                    title: '体格发育基线',
                    summary: '体重、吃奶和休息状态已纳入本周照护观察。',
                    result: '首份体格基线和后续观察重点',
                    owner: '你和照护师',
                    resultAvailable: true,
                    date: '2026.07.19',
                    completed: '已记录近期体重，并确认吃奶与休息状态平稳。',
                    next: '本周继续记录体格状态，作为神经任务的前提。'
                },
                {
                    id: 'assessment',
                    icon: 'eye-o',
                    state: '已完成',
                    status: 'done',
                    title: '3 月龄发展观察',
                    summary: '已完成头颈控制、追视、双手相遇和互动观察。',
                    result: '3 月龄发展观察摘要',
                    owner: '照护师',
                    resultAvailable: true,
                    date: '2026.07.20',
                    completed: '已整理本阶段观察事实，不作为疾病或能力诊断。',
                    next: '按宝宝当天状态，开始短时家庭任务。'
                },
                {
                    id: 'development',
                    icon: 'guide-o',
                    state: '需要你完成',
                    status: 'current',
                    title: '3 月龄家庭任务包',
                    summary: '俯卧抬头、双手相遇和回应发声，按状态短时尝试。',
                    result: '本周任务反馈和照护师复核建议',
                    owner: '你和照护师',
                    action: '开始观察'
                },
                {
                    id: 'followup',
                    icon: 'phone-o',
                    state: '本周安排',
                    status: 'future',
                    title: '本周电话回访',
                    summary: '照护师会确认任务反馈和宝宝的日常状态。',
                    result: '本周照护小结和下周行动',
                    owner: '照护师'
                },
                {
                    id: 'plan',
                    icon: 'todo-list-o',
                    state: '等待前序完成',
                    status: 'future',
                    title: '4 月龄任务准备',
                    summary: '照护师会结合本月观察，为下一阶段准备重点。',
                    result: '4 月龄观察重点和家庭任务包',
                    owner: '照护团队'
                }
            ],
            feedbackOptions: [
                { value: 'completed', label: '我已尝试' },
                { value: 'paused', label: '宝宝不舒服，先暂停' },
                { value: 'help', label: '想请照护师看看' }
            ],
            visitSlots: [
                { value: 'wed-am', label: '7 月 23 日 周三', detail: '上午 09:00 - 11:00' },
                { value: 'thu-pm', label: '7 月 24 日 周四', detail: '下午 14:00 - 16:00' },
                { value: 'sat-am', label: '7 月 26 日 周六', detail: '上午 10:00 - 12:00' }
            ],
            callbackOptions: [
                { value: 'today-pm', label: '今天 14:00 - 18:00' },
                { value: 'tomorrow-am', label: '明天 09:00 - 12:00' },
                { value: 'anytime', label: '工作日均可接听' }
            ]
        };
    },
    computed: {
        currentBaby () {
            return this.babies.find(baby => baby.id === this.selectedBaby) || this.babies[0];
        },
        visibleServices () {
            return this.services.filter(service => service.status === 'done');
        }
    },
    methods: {
        goBack () {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }
            this.$router.push('/');
        },
        showServiceTip () {
            this.$toast('这里会持续更新宝宝的服务进展和成果。');
        },
        toggleService (id) {
            this.openServiceId = this.openServiceId === id ? '' : id;
        },
        handleServiceAction (service) {
            if (service.id === 'development') {
                this.showTaskSheet = true;
                return;
            }
            this.$toast('预约时间将在下一步服务中确认。');
        },
        submitTaskFeedback () {
            this.taskDone = true;
            const developmentService = this.services.find(service => service.id === 'development');
            developmentService.state = this.taskFeedback === 'paused' ? '等待照护师确认' : '已收到反馈';
            developmentService.status = 'pending';
            developmentService.summary = this.taskFeedback === 'paused'
                ? '已标记暂停，照护师会确认下一步。'
                : '本周核心任务已反馈，照护师会结合情况调整。';
            developmentService.action = '';
            this.showTaskSheet = false;
            this.$toast.success('反馈已记录');
        },
        confirmVisit () {
            this.visitRequested = true;
            this.showVisitSheet = false;
            this.$toast.success('预约已提交，照护师会和你确认');
        },
        requestCallback () {
            this.callbackRequested = true;
            this.showCallbackSheet = false;
            this.$toast.success('已申请提前回访，照护师会优先联系你');
        },
        submitVideo () {
            this.videoSubmitted = true;
            this.showVideoSheet = false;
            this.$toast.success('视频已提交，等待照护师复核');
        },
        showResult (service) {
            this.activeResult = service;
            this.showResultPopup = true;
        },
        contactCaregiver () {
            this.$toast('已为你打开联系照护师入口');
        }
    }
};
</script>

<style lang="scss" scoped>
$ink: #203733;
$muted: #71807b;
$line: #dce5de;
$paper: #f8f8f4;
$mint: #157866;
$mint-soft: #e4f1ea;
$coral: #d96a4c;

.care-progress-page {
  min-height: 100vh;
  overflow-x: hidden;
  color: $ink;
  background: $paper;
  font-family: "Noto Serif SC", "Songti SC", "STSong", serif;
  padding: env(safe-area-inset-top) 0 calc(30px + env(safe-area-inset-bottom));
}

.page-header {
  height: 54px;
  display: grid;
  grid-template-columns: 48px 1fr 48px;
  align-items: center;
  padding: 0 10px;
}

.header-title {
  text-align: center;
  font-family: "PingFang SC", "Hiragino Sans GB", sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0;
}

.header-icon,
.phone-button,
.close-popup {
  border: 0;
  background: transparent;
  color: $ink;
  cursor: pointer;
}

.header-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  font-size: 21px;
}

.baby-switcher {
  min-height: 48px;
  margin: 0 20px;
  border-top: 1px solid $line;
  border-bottom: 1px solid $line;
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "PingFang SC", "Hiragino Sans GB", sans-serif;
}

.baby-option {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #9aa6a0;
  font-size: 13px;
  cursor: pointer;
}

.baby-option.active { color: $ink; font-weight: 600; }
.baby-avatar { width: 20px; height: 20px; border-radius: 50%; display: grid; place-items: center; font-size: 10px; color: #fff; }
.baby-avatar.mint { background: #58a995; }
.baby-avatar.peach { background: #d98e73; }
.service-window { margin-left: auto; color: $muted; font-size: 11px; white-space: nowrap; }

.welcome-block { padding: 34px 24px 28px; }
.eyebrow, .section-kicker { margin: 0; color: $mint; font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; font-size: 11px; font-weight: 600; letter-spacing: .06em; }
.welcome-block h1 { margin: 8px 0 11px; font-size: 29px; line-height: 1.32; font-weight: 600; letter-spacing: 0; }
.welcome-copy { max-width: 310px; margin: 0; color: $muted; font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; font-size: 13px; line-height: 1.7; }

.physical-foundation { margin: 0 16px 15px; padding: 17px 18px 15px; border-top: 2px solid $mint; background: #edf5ef; }
.foundation-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }.foundation-head h2 { margin: 4px 0 0; font-size: 19px; font-weight: 600; }.foundation-state { margin-top: 2px; color: $mint; font: 11px "PingFang SC", sans-serif; white-space: nowrap; }.foundation-state .van-icon { margin-right: 2px; }
.foundation-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 17px; }.foundation-metrics span { min-width: 0; color: $muted; font: 11px "PingFang SC", sans-serif; }.foundation-metrics b { color: $ink; font: 600 19px Georgia, "Times New Roman", serif; }.foundation-metrics span:nth-child(3) b { font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; font-size: 16px; }.foundation-metrics small { display: block; margin-top: 4px; color: #77867e; font-size: 10px; }

.focus-task { margin: 0 16px; padding: 17px 18px 18px; background: #fffdfa; border: 1px solid #cadfd4; border-radius: 8px; box-shadow: 0 9px 24px rgba(35, 67, 55, .08); }
.focus-task.completed { border-color: #c8d8dd; }
.task-topline { display: flex; align-items: center; gap: 7px; color: $mint; font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; font-size: 12px; font-weight: 600; }
.live-dot { width: 7px; height: 7px; border-radius: 50%; background: $coral; box-shadow: 0 0 0 4px #f9e4da; }
.completed .live-dot { background: #5793a2; box-shadow: 0 0 0 4px #e3eff2; }
.task-stage { margin-left: auto; color: #8a9690; font-weight: 400; }
.task-main { display: flex; align-items: center; gap: 16px; margin: 16px 0; }
.motion-illustration { position: relative; width: 74px; height: 66px; flex: 0 0 74px; overflow: hidden; border: 1px solid #a7c9bc; border-radius: 7px; background: #e8f2eb; }.motion-head { position: absolute; top: 12px; left: 27px; width: 19px; height: 19px; border: 2px solid #55776b; border-radius: 50%; background: #f5d4bd; }.motion-body { position: absolute; top: 31px; left: 20px; width: 34px; height: 19px; border: 2px solid #55776b; border-radius: 50% 50% 40% 40%; background: #8fc4b1; transform: rotate(-8deg); }.motion-arm { position: absolute; top: 36px; left: 10px; width: 22px; height: 2px; background: #55776b; transform: rotate(-24deg); transform-origin: right; }.motion-floor { position: absolute; right: 8px; bottom: 8px; left: 8px; border-top: 2px solid #85aa9c; }
.scale-illustration { width: 74px; height: 66px; flex: 0 0 74px; position: relative; border: 2px solid $ink; border-radius: 15px 15px 9px 9px; background: #dbeee5; box-shadow: inset 0 -11px 0 rgba(18, 84, 66, .08); }
.scale-screen { position: absolute; top: 9px; left: 16px; width: 41px; height: 19px; line-height: 19px; border: 1px solid #a9c7ba; border-radius: 3px; background: #f9fcf7; text-align: center; font-family: monospace; font-size: 11px; }
.scale-unit { position: absolute; top: 30px; width: 100%; text-align: center; color: #55786e; font: 9px "PingFang SC", sans-serif; }
.scale-illustration i { position: absolute; bottom: 7px; width: 4px; height: 4px; border-radius: 50%; background: #7ca89b; }
.scale-illustration i:nth-of-type(1) { left: 16px; }.scale-illustration i:nth-of-type(2) { left: 34px; }.scale-illustration i:nth-of-type(3) { right: 16px; }
.task-copy h2 { margin: 0 0 5px; font-size: 18px; line-height: 1.3; font-weight: 600; }.task-copy p { margin: 0; color: $muted; font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; font-size: 12px; line-height: 1.6; }
.task-guardrails { display: grid; grid-template-columns: 1fr; gap: 7px; margin: 0 0 15px; padding: 10px 0; border-top: 1px solid #dfebe7; border-bottom: 1px solid #dfebe7; color: #687871; font: 11px/1.5 "PingFang SC", sans-serif; }.task-guardrails span { display: grid; grid-template-columns: 73px 1fr; gap: 5px; }.task-guardrails i { color: $mint; font-style: normal; }.task-guardrails span:last-child i { color: $coral; }
.primary-action, .submit-weight, .sheet-close { width: 100%; min-height: 45px; border: 0; border-radius: 5px; background: $mint; color: #fff; font: 600 14px "PingFang SC", "Hiragino Sans GB", sans-serif; cursor: pointer; }.primary-action .van-icon { margin-left: 7px; vertical-align: -1px; }
.pending-action { display: flex; align-items: center; gap: 7px; padding: 12px 0 0; border-top: 1px solid #dfebe7; color: #517c89; font: 13px "PingFang SC", "Hiragino Sans GB", sans-serif; }

.growth-section, .timeline-section { padding: 42px 20px 0; }.current-status-section { padding-top: 36px; }.section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; }.section-heading h2 { margin: 4px 0 0; font-size: 20px; font-weight: 600; line-height: 1.35; }.data-state { padding: 5px 8px; color: #637a72; border: 1px solid #d5e2da; background: #f1f6f1; border-radius: 3px; font: 11px "PingFang SC", sans-serif; white-space: nowrap; }
.growth-panel { margin-top: 16px; border-top: 1px solid $ink; border-bottom: 1px solid $line; }.growth-chart { height: 117px; position: relative; margin: 0 4px 0 31px; }.chart-grid { position: absolute; inset: 20px 0 12px; background: repeating-linear-gradient(to bottom, transparent 0, transparent 22px, #e5ebe6 23px); border-left: 1px solid #bdc9c1; }.chart-label { position: absolute; left: -31px; color: #96a19b; font: 10px "PingFang SC", sans-serif; }.label-top { top: 17px; }.label-bottom { bottom: 8px; }.chart-empty-marker { position: absolute; top: 47px; left: calc(50% - 13px); display: grid; place-items: center; width: 27px; height: 27px; border-radius: 50%; background: #edf2ed; color: #9db1a8; font-size: 16px; }.chart-point { position: absolute; bottom: 11px; left: 63%; height: 83px; border-left: 1px dashed $mint; }.chart-point span { position: absolute; bottom: 30px; left: -5px; width: 9px; height: 9px; border-radius: 50%; background: $coral; box-shadow: 0 0 0 4px #fde9e1; }.chart-point b { position: absolute; bottom: 7px; left: -11px; color: $mint; white-space: nowrap; font: 10px "PingFang SC", sans-serif; }
.growth-note { display: flex; gap: 11px; padding: 15px 0; }.note-mark { color: $coral; font: 600 12px "PingFang SC", sans-serif; }.growth-note p { margin: 0; color: $muted; font: 12px/1.7 "PingFang SC", "Hiragino Sans GB", sans-serif; }

.professional-loop-section { padding: 38px 20px 0; }.professional-loop { margin-top: 16px; padding: 16px; border: 1px solid #b9d5cb; border-top: 3px solid #355f55; background: #f0f6f2; }.professional-head { display: flex; align-items: center; gap: 9px; }.caregiver-avatar.small { width: 31px; height: 31px; flex: 0 0 31px; font-size: 13px; }.professional-head > div { min-width: 0; }.professional-head b { font: 600 13px "PingFang SC", sans-serif; }.professional-head p { margin: 3px 0 0; color: $muted; font: 10px "PingFang SC", sans-serif; }.review-state { margin-left: auto; padding: 4px 6px; border: 1px solid #b7d2c6; color: $mint; font: 10px "PingFang SC", sans-serif; white-space: nowrap; }.care-loop-steps { display: flex; align-items: center; margin-top: 16px; color: #708078; font: 10px "PingFang SC", sans-serif; white-space: nowrap; }.care-loop-steps span.active { color: $mint; font-weight: 600; }.care-loop-steps i { height: 1px; flex: 1; margin: 0 4px; background: #a9c8bb; }.professional-note { margin: 14px 0 0; padding-top: 12px; border-top: 1px solid #d7e5dc; color: #60726a; font: 11px/1.6 "PingFang SC", sans-serif; }.package-assurance { display: grid; grid-template-columns: repeat(3, 1fr); margin-top: 1px; padding: 13px 8px; border: 1px solid #d5e0da; border-top: 0; background: #fffdfa; }.package-assurance span { min-width: 0; text-align: center; border-right: 1px solid #e1e8e3; }.package-assurance span:last-child { border-right: 0; }.package-assurance b { display: block; color: #355f55; font: 600 14px Georgia, "Times New Roman", serif; }.package-assurance small { display: block; margin-top: 4px; color: $muted; font: 10px "PingFang SC", sans-serif; white-space: nowrap; }

.care-now-section, .upcoming-section { padding: 42px 20px 0; }.current-service-card { margin-top: 16px; padding: 16px; border: 1px solid #cbded3; border-radius: 7px; background: #fffdfa; }.current-service-card + .current-service-card { margin-top: 12px; }.task-service { border-top: 3px solid $mint; }.video-service { border-top: 3px solid #6885a0; }.visit-service { border-top: 3px solid #d79572; }.service-card-head { display: flex; justify-content: space-between; align-items: center; font: 11px "PingFang SC", sans-serif; }.service-index { color: #738078; }.active-label, .appointment-label, .video-label { padding: 4px 7px; border-radius: 3px; background: #eaf5ee; color: $mint; }.appointment-label { background: #fff2ea; color: #bb6548; }.video-label { background: #edf2f7; color: #52718c; }.current-service-card .task-main { margin: 15px 0; }.next-step-line { display: grid; grid-template-columns: 58px minmax(0, 1fr) auto; align-items: center; gap: 7px; padding-top: 12px; border-top: 1px solid #e1e8e3; font: 12px "PingFang SC", sans-serif; }.next-step-line span { color: $muted; }.next-step-line b { overflow: hidden; color: $ink; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }.next-step-line button, .link-action { padding: 0; border: 0; background: transparent; color: $mint; font: 12px "PingFang SC", sans-serif; cursor: pointer; white-space: nowrap; }.next-step-line .van-icon { vertical-align: -1px; }.visit-service-body { display: flex; gap: 13px; align-items: flex-start; margin: 15px 0 12px; }.visit-service-body h2 { margin: 0 0 5px; font-size: 18px; font-weight: 600; }.visit-service-body p { margin: 0; color: $muted; font: 12px/1.6 "PingFang SC", sans-serif; }.visit-icon, .video-icon { width: 41px; height: 41px; flex: 0 0 41px; display: grid; place-items: center; border-radius: 50%; background: #fff0e8; color: #c46d4e; font-size: 20px; }.video-icon { background: #edf2f7; color: #52718c; }.visit-window { padding: 10px; color: #6a7771; background: #f4f7f4; font: 11px "PingFang SC", sans-serif; }.visit-window .van-icon { margin-right: 4px; color: $mint; }.full-action { margin-top: 12px; }.upcoming-list { margin-top: 16px; border-top: 1px solid $ink; }.upcoming-item { display: grid; grid-template-columns: 76px minmax(0, 1fr); gap: 10px; padding: 15px 0; border-bottom: 1px solid $line; }.upcoming-date { padding-top: 2px; color: #a05d46; font: 11px/1.45 "PingFang SC", sans-serif; }.upcoming-copy { min-width: 0; }.upcoming-copy .service-state { margin-bottom: 4px; color: #7f8d86; font-weight: 500; }.upcoming-copy h3 { margin: 0 0 4px; font-size: 16px; font-weight: 600; }.upcoming-copy p { margin: 0; color: $muted; font: 12px/1.6 "PingFang SC", sans-serif; }.callback-item { grid-template-columns: 76px minmax(0, 1fr); }.callback-item .link-action { grid-column: 2; justify-self: start; margin-top: 2px; font-weight: 600; }

.assessment-service { border-top: 3px solid #355f55; }.assessment-icon { width: 53px; height: 53px; flex: 0 0 53px; display: grid; place-items: center; border-radius: 7px; background: #e7f1eb; color: #356554; font-size: 24px; }.assessment-items { display: flex; flex-wrap: wrap; gap: 7px; margin: 0 0 12px; }.assessment-items span { padding: 5px 7px; border: 1px solid #d3e1d9; background: #f4f8f5; color: #587066; font: 10px "PingFang SC", sans-serif; }.upcoming-item, .callback-item { grid-template-columns: 96px minmax(0, 1fr); }.package-assurance b { font-size: 14px; }.care-loop-steps span.active { color: $mint; font-weight: 600; }

.timeline-heading { align-items: center; }.text-action, .result-link { border: 0; padding: 0; background: transparent; color: $mint; font: 12px "PingFang SC", sans-serif; cursor: pointer; }.text-action .van-icon, .result-link .van-icon { margin-left: 3px; vertical-align: -1px; }.timeline-list { padding-top: 19px; }.completed-list { padding-top: 16px; }.service-item { display: flex; min-height: 92px; }.timeline-rail { width: 35px; flex: 0 0 35px; display: flex; flex-direction: column; align-items: center; }.timeline-node { width: 25px; height: 25px; display: grid; place-items: center; border: 1px solid #c8d2cb; border-radius: 50%; background: #f8f8f4; color: #7f9088; font-size: 14px; z-index: 1; }.timeline-line { width: 1px; flex: 1; background: #d7dfda; }.service-item.done .timeline-node { background: $mint; color: #fff; border-color: $mint; }.service-item.current .timeline-node { border: 2px solid $coral; color: $coral; box-shadow: 0 0 0 4px #f9e8e0; }.service-content { width: calc(100% - 35px); padding: 0 0 20px; }.service-summary { width: 100%; display: flex; justify-content: space-between; gap: 10px; padding: 0; border: 0; background: transparent; text-align: left; cursor: pointer; }.service-state { display: block; margin-bottom: 5px; color: $mint; font: 600 11px "PingFang SC", sans-serif; }.current .service-state { color: $coral; }.future .service-state { color: #8e9b95; }.service-summary h3 { margin: 0 0 4px; font-size: 16px; font-weight: 600; }.service-summary p { margin: 0; color: $muted; font: 12px/1.55 "PingFang SC", sans-serif; }.service-arrow { padding-top: 20px; color: #94a09a; }.service-detail { padding-top: 14px; font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; }.detail-rule { width: 100%; height: 1px; margin-bottom: 12px; background: #e1e7e2; }.detail-row { display: flex; justify-content: space-between; gap: 18px; margin: 7px 0; color: $muted; font-size: 11px; line-height: 1.5; }.detail-row strong { color: $ink; text-align: right; font-weight: 500; }.secondary-action { width: 100%; min-height: 38px; margin-top: 10px; border: 1px solid #94b9aa; border-radius: 4px; color: $mint; background: #f5faf7; font: 13px "PingFang SC", sans-serif; cursor: pointer; }.secondary-action .van-icon { margin-left: 6px; vertical-align: -1px; }.result-link { margin-top: 10px; font-weight: 600; }

.caregiver-note { display: flex; align-items: center; gap: 11px; margin: 23px 20px 0; padding: 16px 0; border-top: 1px solid $line; border-bottom: 1px solid $line; }.caregiver-avatar { width: 37px; height: 37px; flex: 0 0 37px; display: grid; place-items: center; border-radius: 50%; background: #354b46; color: #fff; font: 15px "PingFang SC", sans-serif; }.caregiver-note .section-kicker { font-size: 10px; }.caregiver-note h2 { margin: 2px 0; font-size: 14px; font-weight: 600; }.caregiver-note p:last-child { margin: 0; color: $muted; font: 11px/1.4 "PingFang SC", sans-serif; }.phone-button { width: 37px; height: 37px; margin-left: auto; display: grid; place-items: center; border: 1px solid #b9cbc1; border-radius: 50%; color: $mint; font-size: 17px; }

.weight-popup, .result-popup, .action-popup { border-radius: 14px 14px 0 0; }.weight-form, .result-sheet, .action-sheet { padding: 10px 23px calc(24px + env(safe-area-inset-bottom)); background: #fffdfa; font-family: "PingFang SC", "Hiragino Sans GB", sans-serif; }.popup-handle { width: 34px; height: 4px; margin: 0 auto 19px; border-radius: 2px; background: #d6ddd8; }.popup-heading { display: flex; justify-content: space-between; align-items: flex-start; }.popup-heading h2, .result-sheet h2 { margin: 5px 0 22px; color: $ink; font-family: "Noto Serif SC", "Songti SC", serif; font-size: 21px; line-height: 1.35; }.close-popup { width: 32px; height: 32px; display: grid; place-items: center; color: #83908a; font-size: 18px; }.field-label { display: block; margin-bottom: 9px; color: #65746d; font-size: 12px; }.weight-input-wrap { position: relative; border-bottom: 2px solid $ink; }.weight-input-wrap input { width: calc(100% - 42px); height: 48px; padding: 0; border: 0; outline: none; background: transparent; color: $ink; font: 30px "Georgia", "Times New Roman", serif; }.weight-input-wrap span { position: absolute; right: 2px; bottom: 12px; color: $muted; font-size: 14px; }.measurement-reminder { display: flex; gap: 6px; margin: 13px 0 21px; color: $muted; font-size: 11px; line-height: 1.5; }.measurement-reminder .van-icon { padding-top: 1px; color: $mint; }.result-badge { display: inline-block; padding: 4px 7px; border-radius: 3px; background: $mint-soft; color: $mint; font-size: 11px; }.result-meta { margin: -14px 0 18px; color: $muted; font-size: 11px; }.result-content { padding: 15px; border-left: 2px solid #a6cbbd; background: #f3f7f3; color: #42534d; font-size: 13px; line-height: 1.7; }.result-content p { margin: 0 0 12px; }.result-content p:last-child { margin-bottom: 0; }.result-label { color: $mint; font-size: 11px; font-weight: 600; }.sheet-close { margin-top: 20px; }
.task-sheet-description { margin: -10px 0 19px; color: $muted; font-size: 13px; line-height: 1.65; }.feedback-options { display: grid; gap: 8px; }.feedback-options button { min-height: 42px; padding: 0 13px; border: 1px solid #d2ddd6; border-radius: 4px; background: #fff; color: #465750; text-align: left; font: 13px "PingFang SC", sans-serif; cursor: pointer; }.feedback-options button.selected { border-color: $mint; background: #edf6f0; color: $mint; box-shadow: inset 3px 0 0 $mint; }
.time-options { display: grid; gap: 8px; margin-bottom: 19px; }.time-options button { display: flex; justify-content: space-between; align-items: center; min-height: 50px; padding: 8px 13px; border: 1px solid #d2ddd6; border-radius: 4px; background: #fff; color: #465750; text-align: left; cursor: pointer; }.time-options b { font: 13px "PingFang SC", sans-serif; }.time-options small { color: $muted; font: 11px "PingFang SC", sans-serif; }.time-options button.selected { border-color: $mint; background: #edf6f0; color: $mint; box-shadow: inset 3px 0 0 $mint; }
.video-upload-placeholder { min-height: 105px; display: grid; place-items: center; align-content: center; gap: 6px; margin-bottom: 14px; border: 1px dashed #99b2c7; background: #f3f6f9; color: #55738e; }.video-upload-placeholder .van-icon { font-size: 25px; }.video-upload-placeholder b { font: 600 13px "PingFang SC", sans-serif; }.video-upload-placeholder small { color: #748895; font: 10px "PingFang SC", sans-serif; }

@media (min-width: 600px) { .care-progress-page { max-width: 480px; margin: 0 auto; box-shadow: 0 0 0 1px #e3e7e3, 0 18px 50px rgba(25, 54, 43, .10); } }
</style>
